import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppTourStep, TourId, tourStepsById } from "./providerOnboardingTour";

type TourContextValue = {
  currentStepId: string | null;
  isActive: boolean;
  startTour: (tourId: TourId, stepId?: string) => void;
  completeStep: (stepId?: string) => void;
  stopTour: () => void;
  isStepActive: (stepId: string) => boolean;
};

const TourContext = createContext<TourContextValue | null>(null);

const matchesRoute = (step: AppTourStep, pathname: string) => {
  if (step.route && step.route !== pathname) {
    return false;
  }

  if (step.routeIncludes && !step.routeIncludes.every((segment) => pathname.includes(segment))) {
    return false;
  }

  return true;
};

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const tourBaseZIndex = 200000;
  const location = useLocation();
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const currentSteps = activeTourId ? tourStepsById[activeTourId] : [];

  const isActive = currentStepIndex >= 0;
  const currentStep = isActive ? currentSteps[currentStepIndex] : null;

  useEffect(() => {
    if (!currentStep) {
      setTargetRect(null);
      return;
    }

    if (!matchesRoute(currentStep, location.pathname)) {
      setTargetRect(null);
      return;
    }

    const syncTarget = () => {
      const element = document.querySelector(currentStep.selector) as HTMLElement | null;
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        setTargetRect(element.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
    };

    syncTarget();
    const intervalId = window.setInterval(syncTarget, 250);
    window.addEventListener("resize", syncTarget);
    window.addEventListener("scroll", syncTarget, true);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("resize", syncTarget);
      window.removeEventListener("scroll", syncTarget, true);
    };
  }, [currentStep, location.pathname]);

  const startTour = (tourId: TourId, stepId?: string) => {
    setActiveTourId(tourId);
    const steps = tourStepsById[tourId];
    if (!stepId) {
      setCurrentStepIndex(0);
      return;
    }

    const index = steps.findIndex((step) => step.id === stepId);
    setCurrentStepIndex(index >= 0 ? index : 0);
  };

  const stopTour = () => {
    setActiveTourId(null);
    setCurrentStepIndex(-1);
    setTargetRect(null);
  };

  const completeStep = (stepId?: string) => {
    if (!currentStep) {
      return;
    }

    if (stepId && currentStep.id !== stepId) {
      return;
    }

    if (currentStepIndex >= currentSteps.length - 1) {
      stopTour();
      return;
    }

    setCurrentStepIndex((prev) => prev + 1);
  };

  const goBack = () => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  };

  const isStepActive = (stepId: string) => currentStep?.id === stepId;

  const contextValue = useMemo<TourContextValue>(
    () => ({
      currentStepId: currentStep?.id || null,
      isActive,
      startTour,
      completeStep,
      stopTour,
      isStepActive,
    }),
    [currentStep?.id, isActive, activeTourId, currentStepIndex],
  );

  const viewportPadding = 16;
  const tooltipWidth = Math.min(320, Math.max(window.innerWidth - viewportPadding * 2, 260));
  const tooltipHeight = Math.min(220, Math.max(window.innerHeight - viewportPadding * 2, 180));
  const tooltipGap = 18;
  const prefersVerticalPlacement = !!targetRect && (
    targetRect.width >= window.innerWidth * 0.6 || window.innerWidth <= 900
  );
  const isOversizedTarget = !!targetRect && (
    targetRect.width >= window.innerWidth * 0.85 || targetRect.height >= window.innerHeight * 0.6
  );

  let tooltipTop = Math.max((window.innerHeight - tooltipHeight) / 2, 80);
  let tooltipLeft = Math.max((window.innerWidth - tooltipWidth) / 2, 16);

  if (targetRect && !isOversizedTarget) {
    const spaces = {
      right: window.innerWidth - targetRect.right - viewportPadding,
      left: targetRect.left - viewportPadding,
      below: window.innerHeight - targetRect.bottom - viewportPadding,
      above: targetRect.top - viewportPadding,
    };

    const canPlaceRight = spaces.right >= tooltipWidth + tooltipGap;
    const canPlaceLeft = spaces.left >= tooltipWidth + tooltipGap;
    const canPlaceBelow = spaces.below >= tooltipHeight + tooltipGap;
    const canPlaceAbove = spaces.above >= tooltipHeight + tooltipGap;

    if (prefersVerticalPlacement && canPlaceBelow) {
      tooltipTop = targetRect.bottom + tooltipGap;
      tooltipLeft = Math.min(
        Math.max(viewportPadding, targetRect.left + (targetRect.width - tooltipWidth) / 2),
        window.innerWidth - tooltipWidth - viewportPadding,
      );
    } else if (prefersVerticalPlacement && canPlaceAbove) {
      tooltipTop = targetRect.top - tooltipHeight - tooltipGap;
      tooltipLeft = Math.min(
        Math.max(viewportPadding, targetRect.left + (targetRect.width - tooltipWidth) / 2),
        window.innerWidth - tooltipWidth - viewportPadding,
      );
    } else if (canPlaceRight) {
      tooltipLeft = targetRect.right + tooltipGap;
      tooltipTop = Math.min(
        Math.max(viewportPadding, targetRect.top + (targetRect.height - tooltipHeight) / 2),
        window.innerHeight - tooltipHeight - viewportPadding,
      );
    } else if (canPlaceLeft) {
      tooltipLeft = targetRect.left - tooltipWidth - tooltipGap;
      tooltipTop = Math.min(
        Math.max(viewportPadding, targetRect.top + (targetRect.height - tooltipHeight) / 2),
        window.innerHeight - tooltipHeight - viewportPadding,
      );
    } else if (canPlaceBelow) {
      tooltipTop = targetRect.bottom + tooltipGap;
      tooltipLeft = Math.min(
        Math.max(viewportPadding, targetRect.left + (targetRect.width - tooltipWidth) / 2),
        window.innerWidth - tooltipWidth - viewportPadding,
      );
    } else if (canPlaceAbove) {
      tooltipTop = targetRect.top - tooltipHeight - tooltipGap;
      tooltipLeft = Math.min(
        Math.max(viewportPadding, targetRect.left + (targetRect.width - tooltipWidth) / 2),
        window.innerWidth - tooltipWidth - viewportPadding,
      );
    } else {
      const rightPlacementOverlap = Math.max(0, tooltipWidth + tooltipGap - spaces.right);
      const leftPlacementOverlap = Math.max(0, tooltipWidth + tooltipGap - spaces.left);
      const belowPlacementOverlap = Math.max(0, tooltipHeight + tooltipGap - spaces.below);
      const abovePlacementOverlap = Math.max(0, tooltipHeight + tooltipGap - spaces.above);

      const bestPlacement = [
        { side: "right", overlap: rightPlacementOverlap },
        { side: "left", overlap: leftPlacementOverlap },
        { side: "below", overlap: belowPlacementOverlap },
        { side: "above", overlap: abovePlacementOverlap },
      ].sort((a, b) => a.overlap - b.overlap)[0]?.side;

      if (bestPlacement === "right") {
        tooltipLeft = Math.min(targetRect.right + tooltipGap, window.innerWidth - tooltipWidth - viewportPadding);
        tooltipTop = Math.min(
          Math.max(viewportPadding, targetRect.top + (targetRect.height - tooltipHeight) / 2),
          window.innerHeight - tooltipHeight - viewportPadding,
        );
      } else if (bestPlacement === "left") {
        tooltipLeft = Math.max(viewportPadding, targetRect.left - tooltipWidth - tooltipGap);
        tooltipTop = Math.min(
          Math.max(viewportPadding, targetRect.top + (targetRect.height - tooltipHeight) / 2),
          window.innerHeight - tooltipHeight - viewportPadding,
        );
      } else if (bestPlacement === "above") {
        tooltipTop = Math.max(viewportPadding, targetRect.top - tooltipHeight - tooltipGap);
        tooltipLeft = Math.min(
          Math.max(viewportPadding, targetRect.left + (targetRect.width - tooltipWidth) / 2),
          window.innerWidth - tooltipWidth - viewportPadding,
        );
      } else {
        tooltipTop = Math.min(targetRect.bottom + tooltipGap, window.innerHeight - tooltipHeight - viewportPadding);
        tooltipLeft = Math.min(
          Math.max(viewportPadding, targetRect.left + (targetRect.width - tooltipWidth) / 2),
          window.innerWidth - tooltipWidth - viewportPadding,
        );
      }
    }
  }

  tooltipTop = Math.min(
    Math.max(viewportPadding, tooltipTop),
    window.innerHeight - tooltipHeight - viewportPadding,
  );
  tooltipLeft = Math.min(
    Math.max(viewportPadding, tooltipLeft),
    window.innerWidth - tooltipWidth - viewportPadding,
  );

  const highlightTop = targetRect
    ? Math.max(viewportPadding / 2, targetRect.top - 8)
    : 0;
  const highlightLeft = targetRect
    ? Math.max(viewportPadding / 2, targetRect.left - 8)
    : 0;
  const highlightWidth = targetRect
    ? Math.max(0, Math.min(targetRect.width + 16, window.innerWidth - highlightLeft - viewportPadding / 2))
    : 0;
  const highlightHeight = targetRect
    ? Math.max(0, Math.min(targetRect.height + 16, window.innerHeight - highlightTop - viewportPadding / 2))
    : 0;

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {currentStep ? (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.35)",
              zIndex: tourBaseZIndex,
              pointerEvents: "none",
            }}
          />
          {targetRect ? (
            <div
              style={{
                position: "fixed",
                top: highlightTop,
                left: highlightLeft,
                width: highlightWidth,
                height: highlightHeight,
                borderRadius: 18,
                border: "2px solid #f7a64a",
                background: "rgba(255, 255, 255, 0.22)",
                boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.18), 0 0 0 6px rgba(247, 166, 74, 0.14), 0 18px 36px rgba(255, 255, 255, 0.26)",
                backdropFilter: "brightness(1.08) saturate(1.05)",
                zIndex: tourBaseZIndex + 1,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <div
            style={{
              position: "fixed",
              top: tooltipTop,
              left: tooltipLeft,
              width: tooltipWidth,
              maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
              maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
              overflowY: "auto",
              background: "#ffffff",
              border: "1px solid #d9e8f5",
              borderRadius: 20,
              boxShadow: "0 28px 60px rgba(15, 23, 42, 0.22)",
              padding: "1rem",
              zIndex: tourBaseZIndex + 2,
            }}
          >
            <div style={{ color: "#9a3412", fontWeight: 700, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Guided Tour
            </div>
            <div style={{ marginTop: "0.5rem", fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
              {currentStep.title}
            </div>
            <div style={{ marginTop: "0.55rem", color: "#5f7488", lineHeight: 1.7, fontSize: "0.95rem" }}>
              {currentStep.description}
            </div>
            <div style={{ marginTop: "0.8rem", color: "#5f7488", fontSize: "0.84rem" }}>
              {currentStep.actionRequired
                ? "Complete this action to continue."
                : "You can finish here or continue exploring on your own."}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", marginTop: "1rem" }}>
              <button
                type="button"
                onClick={stopTour}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#5f7488",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Skip Tour
              </button>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {currentStepIndex > 0 ? (
                  <button
                    type="button"
                    onClick={goBack}
                    style={{
                      border: "1px solid #d9e8f5",
                      background: "#ffffff",
                      color: "#0f3d63",
                      borderRadius: 999,
                      padding: "0.55rem 0.9rem",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Back
                  </button>
                ) : null}
                {!currentStep.actionRequired ? (
                  <button
                    type="button"
                    onClick={() => completeStep()}
                    style={{
                      border: "none",
                      background: "#f7a64a",
                      color: "#0f172a",
                      borderRadius: 999,
                      padding: "0.55rem 0.95rem",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    {currentStep.nextLabel || "Next"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);

  if (!context) {
    throw new Error("useTour must be used within TourProvider");
  }

  return context;
};
